import polyglotEn from 'ra-language-english';
import { convertRaTranslationsToI18next } from 'ra-i18n-i18next';
import { type TranslationMessages } from 'ra-core';

const en = convertRaTranslationsToI18next(polyglotEn) as TranslationMessages;

/**
 * @see https://www.i18next.com/
 */
export const englishTranslations = {
  ...en,
  ra: {
    ...en.ra,
    message: {
      ...en.ra.message,
      delete_title: 'Delete',
      delete_content:
        'Are you sure you want to delete this item? This action cannot be undone.',
      bulk_delete_title_one: 'Delete item',
      bulk_delete_title_other: 'Delete {{count, number}} items',
      bulk_delete_content_one:
        'Are you sure you want to delete this item? This action cannot be undone',
      bulk_delete_content_other:
        'Are you sure you want to delete these {{count, number}} items? This action cannot be undone',
    },
  },
  resources: {
    users: {
      name_one: 'User',
      name_other: 'Users',
      fields: {
        pictureUrl: 'Profile picture',
        disabledFrom: 'Disabled on',
        jobTitleName: 'Job title',
        departmentName: 'Department',
        regionName: 'Region',
        units: 'Units',
        roles: 'Roles',
        signInMethod: 'Log in',
        internalId: 'Internal ID',
      },
    },
    roles: {
      name_one: 'Role',
      name_other: 'Roles',
      fields: {
        permissionIds: 'Permissions',
      },
    },
    permissions: {
      name: 'Permissions',
    },
    roleUsers: {
      name_one: 'Role User',
      name_other: 'Role Users',
    },
    departments: {
      name_one: 'Department',
      name_other: 'Departments',
      fields: {
        name: 'Name',
      },
    },
    jobTitles: {
      name_one: 'Job Title',
      name_other: 'Job Titles',
      fields: {
        name: 'Name',
      },
    },
    regions: {
      name_one: 'Region',
      name_other: 'Regions',
      fields: {
        name: 'Name',
      },
    },
    units: {
      name_one: 'Unit',
      name_other: 'Units',
      fields: {
        name: 'Name',
        performance: 'Performance',
      },
    },
    topics: {
      name_one: 'Topic',
      name_other: 'Topics',
      fields: {
        formTypeId: 'Form type',
        displayName: 'Name',
      },
    },
    nudges: {
      name_one: 'Nudge',
      name_other: 'Nudge',
      fields: {
        title: 'Title',
        description: 'Description',
        promotedTillDateTime: 'Promoted until',
        duration: 'Duration',
        ['image.src']: 'Image',
        nudgeSlides: 'Slides',
        nudgeFeedbacks: 'Feedback',
        nudgeCategories: 'Categories',
        userGroupIds: 'Visible to',
        assigneeGroupIds: 'Assigned to',
        categoryIds: 'Category',
        assignDateTime: 'Start date',
        dueDateTime: 'Due date',
        createdById: 'Created by',
        createdOn: 'Created on',
        assignedFrom: 'Assigned from',
        dueOn: 'Due on',
        sendReminderOn: 'Send reminder on',
      },
    },
    nudgeCategories: {
      name_one: 'Nudge Category',
      name_other: 'Nudge Categories',
      fields: {
        name: 'Name',
      },
    },
    nudgeSlides: {
      name_one: 'Nudge Slide',
      name_other: 'Nudge Slides',
      fields: {
        content: 'Content',
        ['image.src']: 'Image',
      },
    },
    nudgeFeedbacks: {
      name: 'Nudge Feedback',
      fields: {
        rating: 'Rating',
        isInteresting: 'Is interesting',
      },
    },
    'user-groups': {
      name_one: 'User Group',
      name_other: 'User Groups',
      fields: {
        name: 'Name',
        createdOn: 'Created on',
        rules: 'Rules',
      },
    },
    'user-group-members': {
      name: 'User Group Members',
    },
    posts: {
      name_one: 'Post',
      name_other: 'Posts',
      filters: {
        allTopics: 'All topics',
      },
      fields: {
        id: 'Id',
        createdOn: 'Created on',
        createdById: 'Author',
        topicId: 'Topic',
        postContent: 'Post',
        numberOfViews: 'Views',
        numberOfComments: 'Comments',
        numberOfReactions: 'Likes',
        badge: 'Badge',
        awardedUserId: 'Awarded User',
        richContent: {
          content: 'Content',
        },
        createdAfter: 'Created after',
        createdBefore: 'Created before',
      },
    },
    pulseQuestions: {
      name: 'Pulse Questions',
    },
    badges: {
      name_one: 'Badge',
      name_other: 'Badges',
      fields: {
        imageUrl: 'Badge image',
        name: 'Name',
        image: 'Badge image',
      },
    },
    badgeAwards: {
      name_one: 'Badge award',
      name_other: 'Badge awards',
      fields: {
        awardedToUserId: 'Awarded to',
        awardedToUnit: 'Awarded to unit',
        badgeId: 'Badge',
        awardedByUserId: 'Awarded by',
        awardedByUnit: 'Awarded by unit',
        awardedOn: 'Awarded on',
      },
    },
    kpis: {
      name_one: 'KPI',
      name_other: 'KPIs',
      fields: {
        manageData: 'Manage data',
        addAssignees: 'Assign',
      },
    },
    'kpi-assignees': {
      name_one: 'KPI Assignee',
      name_other: 'KPI Assignees',
    },
    'kpi-data': {
      name: 'KPI Data',
      fields: {
        latestActual: 'Actual',
        latestTarget: 'Target',
        lastUpdated: 'Last updated',
        performance: 'Performance',
        assigneeUnitName: 'Unit',
      },
    },
    journeys: {
      fields: {
        bannerImageUrl: 'Banner image',
        bannerImage: 'Banner image',
        totalActivities: 'Total activities',
        journeyProgressPercentage: 'Done (%)',
        activityStatus: 'Status',
        activityStartedAt: 'Opened on',
        activityMarkedAsDoneAt: 'Done on',
      },
    },
    checklists: {
      fields: {
        title: 'Checklist name',
        unitName: 'Unit',
        unitManagerName: 'Unit Manager',
        assigneeUserGroups: 'Assigned to',
        completionRatio: 'Completion %',
        onTimeRatio: 'On-time %',
        overdueTasksCount: 'Overdue tasks',
        missingPhotosCount: 'Missing photos',
        startOn: 'Start',
        endOn: 'End',
        createdByUserFullName: 'Created by',
        createdOn: 'Date created',
        notifyUsersWhenChecklistStarted:
          'Notify user group(s) at checklist start time',
        notifyUsersWhenChecklistOverdue:
          'Notify user group(s) when checklist becomes overdue',
        userGroupIds: 'Assigned to',
        dueAfterAmount: 'Due after',
        dueAfterUnit: 'Due after unit',
        repeatEveryAmount: 'Repeat every',
        repeatEveryUnit: 'Repeat every unit',
        repeatEveryWeekDays: 'Days',
        recurrenceEndsAfterOccurrences: 'Occurrences',
        checklistSnapshotTitle: 'Checklist',
        unitSnapshotName: 'Unit',
        unitId: 'Units',
        taskSnapshotTitle: 'Task',
        taskStatus: 'Status',
        checklistInstanceStartOn: 'Start at',
        taskCompletedOn: 'Completed at',
        taskCompletedByFirstName: 'First Name',
        taskCompletedByLastName: 'Last Name',
        responses: 'Responses',
      },
    },
  },
  moonstar: {
    appName: 'Moonstar Admin Portal',
    checklists: {
      filters: {
        dateRange: 'Date range',
        customRange: 'Custom range',
        presets: {
          TODAY: 'Today',
          LAST_7_DAYS: 'Last 7 days',
          LAST_30_DAYS: 'Last 30 days',
          LAST_90_DAYS: 'Last 90 days',
          CUSTOM: 'Custom',
        },
      },
      details: {
        noTasks: 'No tasks found using the current filters.',
        title: 'Details',
      },
      overview: {
        noActiveUnits: "This checklist isn't active in any unit.",
        title: 'Overview',
      },
      responses: {
        title: 'Responses',
        view: 'View task responses',
        sections: 'Task response sections',
        response: 'Response',
        photos: 'Photos ({{count}})',
        comment: 'Comment',
        addedBy: 'Added by {{name}}',
        noOptions: 'No options selected.',
        noPhotos: 'No photos were submitted.',
        empty: 'No response or comment was submitted.',
        photo: 'Photo {{number}}',
        previousPhoto: 'Previous photo',
        nextPhoto: 'Next photo',
        showPhoto: 'Show photo {{number}}',
        fullSize: 'Full size',
        download: 'Download',
      },
      taskStatus: {
        COMPLETED: 'Completed',
        PENDING: 'Pending',
        OVERDUE: 'Overdue',
        NOT_COMPLETED: 'Not Completed',
        COMPLETED_LATE: 'Completed Late',
      },
      form: {
        notifications: 'Notifications',
        scheduling: 'Scheduling',
        taskList: 'Task List',
      },
      schedule: {
        dueAfterHelperText: "After the checklist's start time",
        frequency: {
          ONE_OFF: 'One-Off',
          RECURRING: 'Recurring',
        },
        units: {
          MINUTES: 'Minutes',
          HOURS: 'Hours',
          DAYS: 'Days',
          WEEKS: 'Weeks',
        },
        weekDays: {
          SUNDAY: 'Sunday',
          MONDAY: 'Monday',
          TUESDAY: 'Tuesday',
          WEDNESDAY: 'Wednesday',
          THURSDAY: 'Thursday',
          FRIDAY: 'Friday',
          SATURDAY: 'Saturday',
        },
        recurrenceEnds: {
          NEVER: 'Never',
          ON_DATE: 'On date',
          AFTER_OCCURRENCES: 'After occurrences',
        },
      },
      tasks: {
        placeholder: 'Task',
        addOption: 'Add option',
        types: {
          TASK: 'Task',
          OPEN_ENDED: 'Open ended',
          RADIO_SELECT: 'Radio select',
          PHOTO_UPLOAD: 'Photo upload',
        },
      },
    },
    list: {
      clearFilters: 'Clear filters',
    },
    login: {
      welcome:
        'Welcome to Moonstar Admin Portal, where you can manage your employee data. To log in, use the same details that you use to log in to the Moonstar Communication App.',
      email: 'Email',
      phone: 'Phone number',
    },
    import: {
      label: 'Import',
      success: 'Uploaded all items successfully',
      partial_success:
        'Uploaded some items. {{total_failed, number}} had errors and failed to import',
      error: 'Uploading failed. No items were imported',
    },
    bulk_operation: {
      steps: {
        'get-template': 'Prepare',
        upload: 'Upload',
        grid: 'Overview',
        process: 'Summary',
      },
      actions: {
        back: 'Back',
        next: 'Next',
        cancel: 'Cancel',
        startProcessing: 'Start processing',
        done: 'Done',
        exportEdits: 'Export edits',
        exportFailed: 'Export failed',
        clear: 'Clear',
      },
      upload: {
        prompt: 'Drag your CSV / Excel file here',
        browse: 'or browse',
        wrongFile_one: 'Wrong file?',
        wrongFile_other: 'Wrong files?',
        uploadAgain: 'Upload again',
        rowsDetected_one: '{{count, number}} row detected',
        rowsDetected_other: '{{count, number}} rows detected',
        readError:
          'Error reading file. Please ensure it is a valid CSV or Excel file.',
      },
      template: {
        download: 'Download',
        addRecords: "Add the {{resource}} you'd like to process",
        saveAs: 'Save as an Excel or CSV file on your computer',
        dateFormat: 'Dates will be processed using the format DD/MM/YYYY',
        theTemplate: 'the template',
      },
      validation: {
        validating: 'Validating...',
        total: 'Total:',
        invalid: 'Invalid:',
        row: 'row',
      },
      process: {
        progress: 'Processing progress',
        calculating: 'Calculating time remaining...',
        unknown: 'Unknown time remaining',
        lessThanSecond: 'Less than 1 second remaining',
        fewSeconds: 'A few seconds remaining',
        aboutMinute: 'About a minute remaining',
        aboutMinutes: 'About {{count, number}} minutes remaining',
        aboutHours: 'About {{count, number}} hours remaining',
        successTitle: 'Success',
        successMessage:
          '{{success, number}}/{{total, number}} records were processed successfully. You can now close this dialog.',
        partialTitle: 'Partial success',
        partialMessage:
          '{{success, number}}/{{total, number}} records were processed successfully, but {{error, number}} records failed to process. Please export the failed records, check the reasons within, and try again.',
        errorTitle: 'Error',
        errorMessage:
          'All records failed to process. Please export the failed records, check the reasons within, and try again.',
      },
      confirmDiscard: 'Discard changes?',
      confirmStop: 'Stop processing?',
      required: 'REQUIRED',
    },
    bulk_disable: {
      label: 'Bulk Disable',
      success: 'All users disabled successfully',
      partial_success:
        'Disabled some of the users. {{total_failed, number}} had errors and failed.',
      error: 'Disabling failed. No items were imported',
    },
    sms_message: {
      resend: 'Resend welcome SMS',
      success: '{{count, number}} messages enqueued for sending',
      partial_success:
        'Enqueued {{total_success, number}} messages, {{total_failed, number}} had errors and failed to enqueue',
      error: 'Enqueuing messages failed. No messages were enqueued',
    },
    bulk_update: {
      label: 'Bulk Update',
      tooltip: "Must be a '.csv', '.tsv', '.xlsx' or '.xls' file",
      success: '{{count, number}} items updated',
      partial_success:
        'Updated {{total_success, number}} items. {{total_failed, number}} had errors and failed to update',
      error: 'Updating items failed. No items were updated',
    },
    post: {
      preview: {
        recognition: {
          awarded: 'awarded',
          badge: 'Badge',
          hasRecognized: 'has just recognized',
          hasEarned: 'Has earned the',
        },
      },
    },
    user: {
      currentUserLabel: 'You',
      edit: {
        section: {
          personalDetails: 'Personal details',
          companyRelatedInfo: 'Company related info',
          profileSettings: 'Profile settings',
        },
        phoneNumber: {
          requiredText:
            'Phone number is required, because the user cannot sign in with their email',
          enabledHelperText: 'Sign in with phone number is enabled',
          disabledHelperText: 'Sign in with phone number is disabled',
        },
        email: {
          enabledHelperText: 'Sign in with email is enabled',
          disabledHelperText: 'Sign in with email is disabled',
        },
        enable: {
          label: 'Enable',
          success: 'User enabled successfully',
          error: 'Unable to enable user',
        },
        disable: {
          label: 'Disable',
          success: 'User disabled successfully',
          error: 'Unable to disable user',
        },
        signIn: {
          method: 'Used to sign in',
          notMatchingPart1: 'Sign in with',
          notMatchingPart2: 'is enabled',
        },
      },
      filter: {
        labels: {
          users: 'Search users',
        },
        placeholders: {
          users: 'Search by name, email, phone number, or employee ID…',
          departments: 'Search departments…',
          jobTitles: 'Search job titles…',
          regions: 'Search regions…',
          units: 'Search units…',
        },
        userIsDisabled: {
          label: 'Status',
          nullLabel: 'All users',
          trueLabel: 'Disabled',
          falseLabel: 'Enabled',
        },
      },
      disable: {
        disable: 'Disable',
        enable: 'Enable',
        selfDisableExplanation: 'You cannot disable your own account.',
      },
      exportTemplate: {
        button: 'Template',
        tooltip: 'Download import template',
      },
    },
    export: {
      limit:
        'Up to {{max, number}} records can be exported. If you need more, please contact Moonstar Support.',
      email: {
        button: 'Export report',
        description: 'Send the report by email',
        recipients: 'Send report to',
        recipientsHelp:
          'Separate multiple email addresses with a semicolon (;)',
        send: 'Send',
        success: 'The email is on its way, and will be in your inbox shortly',
        error: 'Failed to send email.',
      },
    },
    growAppLink: { copy: 'Copy link' },
    upload: {
      contentUrl: 'Content URL',
      upload: 'Upload',
      description: 'Upload a file or a website to fill in the content URL',
      file: 'Upload file',
      website: 'Upload website',
      dropFile: 'Drag your file here',
      dropWebsite: 'Drag your web content here',
      browse: 'or browse',
      websiteHint:
        "Select a .zip file. It should contain an 'index.html' file as the entry point",
      wrongFile: 'Wrong file?',
      uploadAgain: 'Upload again',
      progress: 'Uploading {{name}} ({{current, number}}/{{total, number}})...',
      success: 'Uploaded successfully!',
      error: 'Failed to upload file: {{error}}',
    },
    dateRange: {
      label: 'Date range',
      clear: 'Clear',
      invalid: 'Invalid date range',
      startDate: 'Start Date',
      endDate: 'End Date',
    },
    dashboard: {
      fetchingData: 'Fetching data...',
      noDataForRange: 'No data available for the selected range.',
      feed: {
        title: 'Feed',
        posts: 'Posts',
        comments: 'Comments',
        reactions: 'Reactions',
        previousSevenDays: '{{count, number}} in previous seven days',
        widgets: {
          posts: {
            title: 'Created Posts',
            description: 'Total number of posts created during selected period',
          },
          comments: {
            title: 'Created Comments',
            description: 'Total number of comments made during selected period',
          },
          reactions: {
            title: 'Reactions',
            description:
              'Total number of reactions added during selected period',
          },
        },
      },
      users: {
        lastSevenDays: '+{{count, number}} in last 7 days',
        comparedToSevenDaysAgo: '{{diff}} compared to 7 days ago',
        percentage: 'Percentage',
        onboarded: {
          title: 'Onboarded Users',
          description:
            'Their details have been imported into Moonstar by an Admin',
          line: 'No. of onboarded users',
        },
        activated: {
          title: 'Activated Users',
          description:
            'They have logged into the Admin Portal or installed and logged into the Moonstar App',
          line: 'No. of activated users',
          percentage: '% out of onboarded',
        },
        engaged: {
          title: 'Engaged Users',
          description:
            'They have used the Admin Portal or Moonstar App within the previous 7 days',
          line: 'No. of engaged users',
          percentage: '% out of activated',
        },
        activationRate: {
          title: 'Activation Rate',
          description: 'Percentage of the activated users out of onboarded',
        },
        engagedThisMonth: {
          title: 'Engaged this month',
          description:
            'Percentage of users engaged in last 30 days out of activated',
        },
      },
      pulseChecks: {
        title: 'Pulse questions',
        answerCount: 'No. Answers',
        question: 'Question',
        answers: 'Answers',
        agree: 'Agree',
        undecided: 'Undecided',
        disagree: 'Disagree',
      },
    },
    unitPerformance: {
      title: '{{name}} - Performance',
      unitScore: 'Unit',
      companyScore: 'Company',
      vsCompany: '{{value}} vs company',
      percentile: {
        top: "You're in the top {{percent}}",
        bottom: "You're in the bottom {{percent}}",
      },
      notAvailable: 'N/A',
      unknownError: 'An unknown error occurred',
      suggestedTraining: {
        title: 'Suggested Training',
        comingSoon: 'Coming soon!',
      },
      kpiData: {
        title: 'KPI Performance Data',
        columns: {
          kpi: 'KPI',
          target: 'Target',
          actual: 'Actual',
          performance: 'Performance',
          vsLastEntry: 'vs Last Entry',
          date: 'Date',
          previousTarget: 'Prev Target',
          previousActual: 'Prev Actual',
          previousPerformance: 'Prev Performance',
          previousDate: 'Prev Date',
        },
      },
      kpiOverview: {
        title: 'KPI Performance Overview',
        performanceSummary: 'Performance Summary',
        previousSummary: 'Previous Summary',
        target: 'Target',
        previousTarget: 'Previous Target',
        actual: 'Actual',
        previousActual: 'Previous Actual',
        performance: 'Performance',
        previousPerformance: 'Previous Performance',
        date: 'Date',
      },
      insights: {
        title: 'Insights',
        pageTitle: '{{name}} - Insights',
        description:
          "Use this space to provide valuable feedback and insights to your employees at {{unit}}. You can save your progress as a draft, or publish it if you're ready to share.",
        status: { noData: 'No Data', draft: 'Draft', published: 'Published' },
        loadError: 'Error loading insights: {{error}}',
        empty: 'No insights available.',
        recent: 'Recent Insights',
        recentEmpty: 'No recent insights available.',
        saveAsDraft: 'Save as draft',
        publish: 'Publish',
        savedAsDraft: 'Insights saved as draft',
        published: 'Insights published',
        saveError: 'Error saving insights: {{error}}',
        saveFailed: 'Something went wrong saving insights',
        emptyContent: 'Content cannot be empty',
      },
      aiInsights: {
        title: 'AI Generated Insights',
        description:
          'AI-powered feedback for the unit based on its most recent performance data.',
        copy: 'Copy insights',
        copied: 'Copied to clipboard',
        copyFailed: 'Failed to copy to clipboard',
        empty: 'No AI generated insights available yet.',
        chat: 'Chat',
        generate: 'Generate new insights',
        generating: 'Generating insights...',
        generateFailed: 'Failed to generate insights',
        generateFailedWithReason: 'Failed to generate insights: {{error}}',
      },
      files: {
        prompt: 'Drop files here or browse',
        hint: 'Up to {{max, number}} files. Upload starts when you generate insights.',
        selected: '{{selected, number}}/{{max, number}} files selected',
        tooMany: 'You can upload up to {{max, number}} files',
        rejected:
          'Some files could not be added. Check the file type and limit.',
        remove: 'Remove {{name}}',
      },
      chat: {
        askQuestion: 'Ask a question',
        send: 'Send',
        newChat: 'Start a new chat',
        chatNumber: 'Chat #{{number}}',
        thinking: 'Thinking...',
        replyFailed: 'Failed to get response. Please try again',
        attachments: 'Attachments',
        noContent: '(No content)',
      },
      promptTest: {
        title: 'Experimental Chat Prompt Test',
        description:
          'Test custom system and user prompts with files on Gemini models',
        newChat: 'New Chat',
        newChatStarted: 'Started a new chat session',
        response: 'Assistant Response',
        emptyResponse: "Send a prompt below to see the assistant's response.",
        model: 'Model',
        modelPlaceholder: 'Specify the model to use...',
        viewModels: 'View available models',
        systemPrompt: 'System Prompt',
        systemPromptPlaceholder:
          'Provide system level behavior instructions...',
        userPrompt: 'User Prompt',
        userPromptPlaceholder: 'Ask your question here...',
      },
    },
    'user-group-members': {
      title: 'Members',
      add: {
        label: 'Add members',
        successMessage: '{{count, number}} members added',
      },
      view: {
        label: 'View members',
      },
      remove: {
        successMessage: '{{count, number}} members removed',
      },
      noMembersAssigned: 'No members yet',
      allMembersAssigned: 'All members are assigned to this user group',
    },
    roles: {
      adminRoleHelperText:
        'This is an admin role, therefore all permissions are automatically granted.',
    },
    roleUsers: {
      title: 'Users',
      userRoleTooltip: 'All users are automatically assigned to the user role',
      add: {
        label: 'Add users',
        successMessage: '{{count, number}} users added',
      },
      remove: {
        successMessage: '{{count, number}} users removed',
      },
      view: {
        label: 'View users',
      },
      noUsersAssigned: 'No users yet',
      allUsersAssigned: 'All users are assigned to this role',
    },
    kpis: {
      nameHelperText:
        'Please use succinct, contextually relevant names for KPIs. Keep in mind that KPI title is taken into account for generating AI Insights',
      descriptionHelperText:
        "This description helps AI understand the KPI's purpose and provide contextual feedback and insights",
      format: {
        number: 'Number',
        percentage: 'Percentage',
        star_rating: 'Star Rating',
        currency: 'Currency',
      },
    },
    kpiAssignees: {
      add: {
        successMessage: '{{count, number}} assignees added',
      },
      remove: {
        successMessage: '{{count, number}} assignees removed',
      },
      noAssignees: 'No assignees yet',
      noUnassigned: 'Everyone has already been assigned to this KPI',
      addAssignees: 'Add assignees',
      viewAssignees: 'View assignees',
    },
    kpiData: {
      lastUpdatedNever: 'Never',
      title: 'Data',
      import: {
        unitNotFound:
          'Unit with this name does not exist or has not been assigned to this KPI.',
        unexpectedError:
          'Unexpected error. Please reach out to Moonstar Support.',
      },
    },
    analytics: {
      name: 'Analytics',
    },
    menu: {
      brand: 'Moonstar',
      dashboard: 'Dashboard',
      users: 'Users',
      feed: 'Feed',
      pulseChecks: 'Pulse Checks',
      chat: 'Chat',
      unreadMessages: 'Unread messages',
      company: 'Company',
    },
    validation: {
      phoneNumber:
        'Not a valid phone number. Must be in format: +[countryCode][number]. E.g. +40712345678',
      maxFileSize: 'File size must be less than {{maxSize}}',
      invalidUrl: 'Invalid URL',
    },
    never: 'Never',
    nudges: {
      userGroupIdsHelperText: 'Select which user groups to show this nudge to',
      promotedTillDateTimeHelperText:
        'Promoted nudges are listed above other nudges in the app',
      list: {
        categoryCount_one: '{{count, number}} category',
        categoryCount_other: '{{count, number}} categories',
        groupCount_one: '{{count, number}} group',
        groupCount_other: '{{count, number}} groups',
      },
      actions: {
        sendReminder: 'Send reminder',
        duplicate: 'Duplicate',
        report: 'Report',
      },
      remind: {
        success:
          'Reminder notification is on its way and will be sent shortly to everyone who needs to be reminded.',
        error: 'The reminder could not be sent: {{message}}',
      },
      duplicate: {
        error: 'The nudge could not be duplicated: {{message}}',
      },
      report: {
        error: 'The requested report could not be downloaded: {{message}}',
      },
      assignment: {
        title: 'Assignment',
        description:
          'Assign this nudge to specific user groups from the "Visible to" list with a shared schedule. To use different dates, duplicate the nudge and set new dates on each one.',
        assigneeGroupIdsHelperText:
          'Select which user groups to assign this nudge to',
        assignedFromHelperText:
          'When the nudge appears in the app for the user groups',
        dueOnHelperText: 'Deadline to complete the nudge',
        sendReminderOnHelperText:
          "Send a notification to the users who haven't completed the nudge yet",
        validation: {
          invalidDate: 'Value must be a valid date',
          futureDate: 'Value must be in the future',
          onOrAfter: 'Value must be on or after {{date}}',
          onOrBefore: 'Value must be on or before {{date}}',
        },
      },
      slides: {
        type: {
          label: 'Slide type',
          withContentAndImage: 'Quick note',
          withUrl: 'External Link',
        },
        imageOrContentRequired: 'Image or content must be set',
        externalLink: {
          title: 'External links are currently mobile-only.',
          description:
            'Web users will see a prompt to download the mobile app to view this content.',
        },
        maxSlides: 'You can have up to {{max, number}} slides per nudge',
        noReordering: {
          title: 'We do not currently support changing the order of slides',
          description:
            'Please create / edit / delete your slides to get them into the order you need. Please reach out to us if you need any support with this. Thank you for your patience',
        },
      },
    },
    badges: {
      noDeletion: {
        title: 'We do not currently support badge deletion',
        description:
          "Once a badge is created, you will not be able to delete it directly. You can, however, edit the badge name and image later if needed. For badge removal, you'll need to contact Moonstar support",
      },
      analytics: {
        showDaysWithoutAwards: 'Show Days Without Awards',
        selectedBadgesAwards: 'Selected badges awards',
        allBadgesAwards: 'All badges awards',
        singleBadgeTitle: 'Badge awards: {{name}}',
      },
    },
    journeys: {
      addSection: 'Add a section',
      addActivity: 'Add an activity',
      section: 'Section',
      activity: 'Activity',
      activityType: 'Type',
      activityTypes: {
        course: 'Course',
        inPerson: 'In person',
        live: 'Live',
        pdf: 'PDF',
        quiz: 'Quiz',
        survey: 'Survey',
        video: 'Video',
      },
      journeyProgress: {
        notStarted: 'Not started',
        inProgress: 'In progress',
        completed: 'Done',
      },
      activityStatus: {
        notStarted: 'Not started',
        opened: 'Opened',
        done: 'Done',
      },
    },
  },
  chat: {
    search: 'Search',
    memberActions: 'Actions for {{name}}',
    searchModes: {
      channelName: 'Search by channel name',
      username: 'Search by employee in channel',
    },
    close: 'Close',
    create: 'Create ({{selected, number}}/{{max, number}})',
    invite: 'Invite ({{selected, number}}/{{max, number}})',
    reply: 'Reply',
    forward: 'Forward',
    pin: 'Pin',
    unpin: 'Unpin',
    edit: 'Edit',
    delete: 'Delete',
    allMembersModalTitle: 'Members of the channel',
    registerAsOperator: 'Register as operator',
    unregisterAsOperator: 'Unregister as operator',
    mute: 'Mute',
    unmute: 'Unmute',
    kick: 'Kick',
    somethingWrong: 'Something went wrong. Please try again later.',
    pinMessage:
      'Message has been pinned. You can pin up to {{maxPinnedMessages, number}} messages. You can find all of your pinned messages in the pinned tab in the group settings.',
    unpinMessage:
      'Message has been unpinned. You can find all of your pinned messages in the pinned tab in the group settings.',
    pinMessageMax:
      'You can pin up to {{maxPinnedMessages, number}} messages. Unpin some messages',
    messageForwarded: 'Message forwarded successfully',
    messageForwardTitle: 'Group(s) you want to share the message with',
    cancel: 'Cancel',
    somethingWentWrong: 'Something went wrong. Please try again later.',
    groupCreationError:
      "Couldn't create a group, please try again later or contact Moonstar support.",
    groupCreationErrorMaximumChannelCount:
      'The limit of 2,000 chats has been reached. Please use an alternate method for contacting them, or contact your manager for assistance. Thank you!',
    groupInviteError:
      "Couldn't invite members to a group, please try again later or contact Moonstar support.",
    groupCreateSubtitle:
      'Create a chat that allows one-to-one communication or a group of up to 100 members.',
    supergroupCreateSubtitle:
      'Create supergroup that allows chat for up to 20,000 members.',
    errorLoadingMoreUsers: 'Error loading more users. Please try again later.',
    retry: 'Retry',
    searchNewGroup: 'Search for members for the new group',
    searchNewSupergroup: 'Search for members for the new supergroup',
    pinnedMessagesCouldNotBeFetched:
      "We couldn't get your pinned messages. Please try again later.",
    pinnedMessagesNotFound: 'You have no pinned messages in this group yet.',
    createGroupsModalTitle: 'Select members to create a group with',
    createGroupsModalNoUsersFound: 'Users not found',
    inviteUsersModalTitle: 'Select members to invite',
    removeUserError:
      'Failed to remove user from the group. Please try again later.',
    muteUserError:
      'Failed to mute user from the group. Please try again later.',
    unmuteUserError:
      'Failed to unmute user from the group. Please try again later.',
    kickMemberError:
      'Failed to kick member from the group. Please try again later.',
    leaveChannelError: 'Failed to leave the channel. Please try again later.',
    registerAsOperatorError:
      'Failed to register user from the group as operator. Please try again later.',
    unregisterAsOperatorError:
      'Failed to unregister user from the group as operator. Please try again later.',
    toggleOperatorsStatusError:
      'Failed to change operators status. Please try again later.',
    inviteToChannelError:
      'Failed to invite users to the channel. Please try again later.',
    addOperator: 'Add operator',
    addOperatorsModalTitle: 'Select members',
    allOperatorsModalTitle: 'All operators',
    searchMembers: 'Search for members',
    searchOperators: 'Search for operators',
    selectedCount: '{{count, number}} selected',
    operator: 'Operator',
    adding: 'Adding...',
    add: 'Add',
    createNewGroup: 'Create a new group',
    discardChannelModalTitle: 'Discard channel',
    discaradChannelModalContent:
      'If you leave this channel without sending a message, the channel will be discarded. Are you sure you want to leave and discard the channel?',
    discard: 'Discard',
    stay: 'Stay',
    loadingMembers: 'Loading all members... {{progressPercentage, number}}%',
    selectUser: 'Select {{name}}',
    muted: 'Muted',
    pinnedMessages: 'Pinned messages',
    searchMessages: 'Search messages',
    channelSettings: 'Channel settings',
    mediaViewer: {
      download: 'Download',
      previous: 'Previous',
      next: 'Next',
      imagePreview: 'Image preview: {{name}}',
      videoPreview: 'Video preview: {{name}}',
      noCaptions: 'No captions',
      description: 'Browse media shared in this channel.',
      loadMoreMediaError: 'Failed to load more media. Please try again later.',
      loadMoreMediaRetry: 'Try again',
    },
  },
};
